import { useRef, useState } from "react";
import { Headphones, Play, Pause, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

const LR_DIRECT_BRIEF_BUCKET = "direct-load-briefs";

interface LRDirectLoadBriefPlayerProps {
  path: string;
  durationSec?: number | null;
}

/**
 * Broker-side LR Direct Load Brief player.
 * Isolated from the marketplace player: reads only the LR Direct bucket.
 * Lazy — no network until the broker clicks play.
 */
export function LRDirectLoadBriefPlayer({ path, durationSec }: LRDirectLoadBriefPlayerProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState<number>(durationSec || 0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const ensureSrc = async (): Promise<string | null> => {
    if (src) return src;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .storage
        .from(LR_DIRECT_BRIEF_BUCKET)
        .createSignedUrl(path, 600);
      if (err || !data?.signedUrl) {
        setError(true);
        return null;
      }
      setSrc(data.signedUrl);
      return data.signedUrl;
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    if (error) return;
    const audio = audioRef.current;
    if (audio && playing) {
      audio.pause();
      return;
    }
    const url = await ensureSrc();
    if (!url) return;
    requestAnimationFrame(() => {
      audioRef.current?.play().catch(() => setError(true));
    });
  };

  const pct = dur > 0 ? Math.min(100, (t / dur) * 100) : 0;
  const displayDur = dur || durationSec || 0;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
      <Headphones className="h-3.5 w-3.5 text-brand-teal-deep shrink-0" />
      <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground shrink-0">
        Load Brief
      </span>

      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleToggle}
        disabled={loading || error}
        className="h-7 gap-1.5 text-[11px] shrink-0"
      >
        {error ? (
          <>
            <AlertCircle className="h-3.5 w-3.5" />
            Unavailable
          </>
        ) : loading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading
          </>
        ) : playing ? (
          <>
            <Pause className="h-3.5 w-3.5" />
            Pause
          </>
        ) : (
          <>
            <Play className="h-3.5 w-3.5" />
            Play
          </>
        )}
      </Button>

      <div className="flex-1 min-w-[40px] h-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-brand-teal-deep transition-[width] duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>

      <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
        {Math.floor(t)}s / {displayDur}s
      </span>

      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setT(0);
          }}
          onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (isFinite(d) && d > 0) setDur(Math.round(d));
          }}
          onError={() => setError(true)}
          className="hidden"
        />
      )}
    </div>
  );
}
